import {getHighlightedEvent, type HighlightedEvent} from '@github-ui/timeline-items/HighlightedEvent'
import {graphql, type LoadMoreFn, useLazyLoadQuery, usePaginationFragment} from 'react-relay'

import type {IssueTimelineViewType} from '../../constants/timeline'
import type {IssueTimelineItem$data} from './__generated__/IssueTimelineItem.graphql'
import type {NewTimelinePaginationHighlightQuery} from './__generated__/NewTimelinePaginationHighlightQuery.graphql'
import type {
  useTimelineHighlightItems$data,
  useTimelineHighlightItems$key,
} from './__generated__/useTimelineHighlightItems.graphql'
import type {useTimelineHighlightQuery} from './__generated__/useTimelineHighlightQuery.graphql'

export const isEventHighlighted = (item: IssueTimelineItem$data, highlight: HighlightedEvent) => {
  // Discard id mismatches
  if (String(item.databaseId) !== highlight.id) return false

  // Discard issue comment highlights if the item is not an issue comment
  if (highlight.prefix === 'issuecomment' && item.__typename !== 'IssueComment') return false

  // Discard event highlights if the item is an issue comment
  if (highlight.prefix === 'event' && item.__typename === 'IssueComment') return false

  return true
}

export type HighlightedData = {
  data: useTimelineHighlightItems$data['timelineItems'] | undefined

  hasPrevious: boolean
  totalBeforeFocus: number
  loadPrevious: LoadMoreFn<NewTimelinePaginationHighlightQuery>

  hasNext: boolean
  totalAfterFocus: number
  loadNext: LoadMoreFn<NewTimelinePaginationHighlightQuery>
}

export const getHighlight = (events: IssueTimelineItem$data[], highlightedEvent: string | undefined) => {
  const highlight = getHighlightedEvent(highlightedEvent)

  const highlightedItem = highlight && events.find(item => isEventHighlighted(item, highlight))

  return {
    highlight,
    isEventLoaded: highlightedItem !== undefined,
  }
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const highlightQuery = graphql`
  query useTimelineHighlightQuery(
    $id: ID!
    $focusText: String!
    $after: String
    $before: String
    $first: Int!
    $last: Int
    $focusNeighborCount: Int
    $viewType: TimelineEventViewType
  ) {
    node(id: $id) {
      ... on Issue {
        ...useTimelineHighlightItems
          @dangerously_unaliased_fixme
          @arguments(
            after: $after
            first: $first
            focusText: $focusText
            last: $last
            before: $before
            focusNeighborCount: $focusNeighborCount
            viewType: $viewType
          )
      }
    }
  }
`

/**
 * Build the variables needed for the highlight query.
 * Exported so callers can preload the query via `fetchQuery` in parallel
 * with other data fetches (e.g. the activity tab lazy-load).
 */
export function buildHighlightQueryVariables(
  issueId: string,
  highlightedEvent: HighlightedEvent | undefined,
  viewType?: IssueTimelineViewType,
): useTimelineHighlightQuery['variables'] {
  return {
    id: issueId,
    focusText: highlightedEvent ? `${highlightedEvent.prefix}-${highlightedEvent.id}` : '',
    first: 1,
    focusNeighborCount: 3,
    viewType,
  }
}

export const useTimelineHighlights = (
  issueId: string,
  highlightedEvent: HighlightedEvent | undefined,
  fetchFromServer: boolean = false,
  viewType?: IssueTimelineViewType,
): HighlightedData => {
  const {node} = useLazyLoadQuery<useTimelineHighlightQuery>(
    highlightQuery,
    buildHighlightQueryVariables(issueId, highlightedEvent, viewType),
    // When not fetching from server, read from the Relay store only.
    // When fetching, use store-or-network so a parallel preload (via fetchQuery)
    // can satisfy the request without a redundant network round-trip.
    {fetchPolicy: fetchFromServer ? 'store-or-network' : 'store-only'},
  )

  const {data, hasNext, hasPrevious, loadNext, loadPrevious} = usePaginationFragment<
    NewTimelinePaginationHighlightQuery,
    useTimelineHighlightItems$key
  >(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment useTimelineHighlightItems on Issue
      @argumentDefinitions(
        after: {type: "String"}
        first: {type: "Int"}
        before: {type: "String"}
        last: {type: "Int"}
        focusText: {type: "String"}
        focusNeighborCount: {type: "Int"}
        viewType: {type: "TimelineEventViewType"}
      )
      @refetchable(queryName: "NewTimelinePaginationHighlightQuery") {
        timelineItems(
          first: $first
          after: $after
          before: $before
          last: $last
          focusText: $focusText
          visibleEventsOnly: true
          focusNeighborCount: $focusNeighborCount
          viewType: $viewType
        ) @connection(key: "timelineBackwards_timelineItems", filters: ["viewType"]) {
          beforeFocusCount
          afterFocusCount
          # Used in the timeline rollup
          edges {
            node {
              __id
              # Used in the timeline component
              # eslint-disable-next-line relay/must-colocate-fragment-spreads
              ...IssueTimelineItem
            }
          }
        }
      }
    `,
    node,
  )

  const totalBeforeFocus = data?.timelineItems?.beforeFocusCount || 0
  const totalAfterFocus = data?.timelineItems?.afterFocusCount || 0

  return {
    data: data?.timelineItems,
    hasPrevious,
    totalBeforeFocus,
    loadPrevious,
    hasNext,
    totalAfterFocus,
    loadNext,
  }
}
